import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { channelManagerText, ChannelManagerText } from '../i18n/pages/channelManager';
import { colors } from '../theme';
import AppButton from './AppButton';
import {
  DuplicateReservation,
  DuplicateSide,
  getDuplicateReservations,
  reuniteReservations,
} from '../api.channelManager';

interface Props {
  apartmentId: number;
  /** Bumped by the parent after a link/unlink/sync so the list refetches. */
  refreshKey?: number;
  /** Lets the parent refresh its own triage list once a merge removes a row. */
  onMerged?: () => void;
}

/**
 * Pairs of records that are one stay, offered for merging.
 *
 * Separate from the triage list on purpose. Triage is a queue of things sync could not decide;
 * this is a queue of things it decided twice, and the fix is not "resolve" but "these are the same
 * booking". The pair is shown side by side with what each record actually holds, because the
 * destructive half of a merge should be a judgement made on evidence rather than on trust in the
 * recommendation — which is why the direction can be swapped before confirming.
 */
const DuplicateReservationList: React.FC<Props> = ({ apartmentId, refreshKey, onMerged }) => {
  const { lang } = useLang();
  const t = channelManagerText[lang];

  const [pairs, setPairs] = useState<DuplicateReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [swapped, setSwapped] = useState<Record<string, boolean>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setPairs(await getDuplicateReservations(apartmentId));
    } catch {
      // Supplementary panel — a failure here must not take the surrounding settings down.
      setPairs([]);
    } finally {
      setLoading(false);
    }
  }, [apartmentId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const merge = async (pairKey: string, keep: DuplicateSide, remove: DuplicateSide) => {
    if (!window.confirm(t.duplicatesConfirm(keep.id, remove.id))) return;
    setBusyKey(pairKey);
    setError('');
    setSuccess('');
    try {
      await reuniteReservations(apartmentId, keep.id, remove.id);
      setSuccess(t.duplicatesMerged);
      // One refetch, not two: a parent that wants to know bumps refreshKey in response, which
      // re-runs the effect below. Only reload directly when nobody is listening.
      if (onMerged) {
        onMerged();
      } else {
        await load();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Merge failed');
    } finally {
      setBusyKey(null);
    }
  };

  if (loading || pairs.length === 0) return null;

  return (
    <div style={{ marginTop: 18 }}>
      <h4 style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 700 }}>
        {t.duplicatesTitle} ({pairs.length})
      </h4>
      <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#4a5568', lineHeight: 1.5 }}>
        {t.duplicatesIntro}
      </p>

      {error && (
        <div style={{ ...noticeStyle, background: colors.badgeRedBg, color: colors.badgeRedText }}>{error}</div>
      )}
      {success && (
        <div style={{ ...noticeStyle, background: colors.badgeGreenBg, color: colors.badgeGreenText }}>
          {success}
        </div>
      )}

      {pairs.map((pair) => {
        // Identifies the pair, not either half — the two ids swap roles when the manager flips the
        // direction, so keying on one of them alone would mean something different after a swap.
        const pairKey = `${pair.keep.id}-${pair.remove.id}`;
        const isSwapped = swapped[pairKey];
        const keep = isSwapped ? pair.remove : pair.keep;
        const remove = isSwapped ? pair.keep : pair.remove;
        const busy = busyKey === pairKey;

        return (
          <div key={pairKey} style={cardStyle}>
            <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: 8 }}>
              {pair.checkInDate} → {pair.checkOutDate}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
              <SideCard side={keep} label={t.duplicatesKeep} tone="keep" t={t} />
              <SideCard side={remove} label={t.duplicatesRemove} tone="remove" t={t} />
            </div>

            {!isSwapped && (
              <div style={{ fontSize: '0.78rem', color: '#718096', marginTop: 8 }}>
                {pair.keepHasGuestData ? t.duplicatesRationaleHasData : t.duplicatesRationaleNeither}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <AppButton
                type="button"
                variant="green"
                size="compact"
                disabled={busy}
                onClick={() => merge(pairKey, keep, remove)}
              >
                {busy ? t.duplicatesReuniting : t.duplicatesReunite}
              </AppButton>
              <AppButton
                type="button"
                variant="blue"
                size="compact"
                disabled={busy}
                onClick={() => setSwapped((s) => ({ ...s, [pairKey]: !s[pairKey] }))}
              >
                {t.duplicatesSwap}
              </AppButton>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** One record, described by what it holds rather than by its columns. */
const SideCard: React.FC<{
  side: DuplicateSide;
  label: string;
  tone: 'keep' | 'remove';
  t: ChannelManagerText;
}> = ({ side, label, tone, t }) => {
  const holdings: string[] = [];
  if (side.formMemberCount > 0) holdings.push(t.duplicatesForm(side.formMemberCount));
  if (side.hasCityTax) holdings.push(t.duplicatesCityTax);
  if (side.hasUbyport) holdings.push(t.duplicatesUbyport);
  if (side.hasCheckinLink) holdings.push(t.duplicatesCheckinLink);
  if (side.hasPmsCode) holdings.push(t.duplicatesPmsCode);

  const accent = tone === 'keep' ? '#38a169' : '#c53030';

  return (
    <div
      style={{
        flex: '1 1 220px',
        minWidth: 0,
        border: `1px solid ${tone === 'keep' ? '#9ae6b4' : '#feb2b2'}`,
        background: tone === 'keep' ? '#f0fff4' : '#fff5f5',
        borderRadius: 8,
        padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {label} · #{side.id}
      </div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#2d3748', margin: '4px 0 2px', overflowWrap: 'anywhere' }}>
        {side.guestName || '—'}
      </div>
      {side.source && (
        <div style={{ fontSize: '0.74rem', color: '#718096', overflowWrap: 'anywhere' }}>{side.source}</div>
      )}
      <div style={{ fontSize: '0.78rem', color: holdings.length ? '#2d3748' : '#a0aec0', marginTop: 6 }}>
        {holdings.length ? holdings.join(' · ') : t.duplicatesNoData}
      </div>
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 14,
  marginBottom: 10,
  background: '#fff',
};

const noticeStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  fontSize: '0.83rem',
  marginBottom: 10,
};

export default DuplicateReservationList;
